import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import { getTokens } from "../../styles/tokens";
import TicketsManagerTabs from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";

const defaultTicketsManagerWidth = 390;
const minTicketsManagerWidth = 320;
const maxTicketsManagerWidth = 470;

const useStyles = makeStyles((theme) => {
	const t = getTokens(theme.palette.type, theme.palette.primary.main);
	return {
		chatContainer: {
			flex: 1,
			// tela cheia, como no WhatsApp Web: sem moldura em volta da conversa
			padding: 0,
			height: "calc(100% - 64px)",
			boxSizing: "border-box",
			overflowY: "hidden",
			backgroundColor: t.page,
			[theme.breakpoints.down("sm")]: {
				height: "calc(100% - 56px)",
				padding: 0,
			},
		},
		chatPapper: {
			display: "flex",
			height: "100%",
			overflow: "hidden",
			border: 0,
			borderTop: `1px solid ${t.line}`,
			borderRadius: 0,
			boxShadow: "none",
			backgroundColor: t.paper,
			[theme.breakpoints.down("sm")]: {
				border: 0,
				borderRadius: 0,
			},
		},
		contactsWrapper: {
			display: "flex",
			height: "100%",
			flexDirection: "column",
			overflowY: "hidden",
			position: "relative",
			backgroundColor: t.paper,
			borderRight: `1px solid ${t.line}`,
			minWidth: `${minTicketsManagerWidth}px`,
		},
		messagesWrapper: {
			display: "flex",
			height: "100%",
			flexDirection: "column",
			flexGrow: 1,
			minWidth: 0,
		},
		// estado vazio no padrão do WhatsApp Web: fundo cinza plano, ícone, título e frase
		welcomeMsg: {
			backgroundColor: theme.palette.type === "dark" ? "#222e35" : "#f0f2f5",
			color: t.textSecondary,
			fontSize: 14,
			display: "flex",
			justifyContent: "space-evenly",
			alignItems: "center",
			height: "100%",
			textAlign: "center",
			border: "none !important",
			borderRadius: 0,
		},
		emptyState: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			maxWidth: 440,
			padding: "36px 28px",
		},
		emptyIcon: {
			display: "flex",
			marginBottom: 18,
			color: t.textMuted,
			"& svg": { fontSize: 72 },
		},
		emptyTitle: {
			marginBottom: 10,
			color: t.text,
			fontSize: 28,
			fontWeight: 300,
			letterSpacing: "-0.01em",
		},
		emptyText: {
			color: t.textSecondary,
			fontSize: 14,
			lineHeight: 1.55,
		},
		dragger: {
			width: 5,
			cursor: "ew-resize",
			position: "absolute",
			top: 0,
			right: -3,
			bottom: 0,
			zIndex: 100,
			backgroundColor: "transparent",
			userSelect: "none",
			transition: "background-color 150ms ease",
			"&:hover, &:active": {
				backgroundColor: t.accent,
			},
		},
	};
});

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	
	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(
		user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth
	);
	
	const classes = useStyles({ ticketsManagerWidth });
	const { ticketId } = useParams();
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);
	const contactsWrapperRef = useRef(null);

	useEffect(() => {
		const initialWidth = user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth;
		
		const validWidth = Math.max(
			minTicketsManagerWidth,
			Math.min(maxTicketsManagerWidth, initialWidth)
		);
		
		setTicketsManagerWidth(validWidth);
		ticketsManagerWidthRef.current = validWidth;
	}, [user]);

	const handleMouseDown = (e) => {
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};

	const handleSaveContact = async (value) => {
		const validValue = Math.max(
			minTicketsManagerWidth,
			Math.min(maxTicketsManagerWidth, value)
		);
		
		try {
			await api.put(`/users/toggleChangeWidht/${user.id}`, { 
				defaultTicketsManagerWidth: validValue 
			});
		} catch (error) {
			console.error("Erro ao salvar largura:", error);
		}
	};

	const handleMouseMove = useCallback((e) => {
		const panelLeft = contactsWrapperRef.current?.getBoundingClientRect().left || 0;
		const newWidth = e.clientX - panelLeft;
		
		if (newWidth >= minTicketsManagerWidth && newWidth <= maxTicketsManagerWidth) {
			ticketsManagerWidthRef.current = newWidth;
			setTicketsManagerWidth(newWidth);
		}
	}, []);

	const handleMouseUp = async () => {
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== ticketsManagerWidth) {
			await handleSaveContact(newWidth);
		}
	};

	const effectiveWidth = Math.max(
		minTicketsManagerWidth,
		Math.min(maxTicketsManagerWidth, ticketsManagerWidth)
	);

	return (
		<QueueSelectedProvider>
			<div className={classes.chatContainer}>
				<div className={classes.chatPapper}>
						<div
							ref={contactsWrapperRef}
							className={classes.contactsWrapper}
						style={{ 
							width: `${effectiveWidth}px`,
							minWidth: `${minTicketsManagerWidth}px`,
							maxWidth: `${maxTicketsManagerWidth}px`,
							opacity: effectiveWidth > 0 ? 1 : 0,
							visibility: effectiveWidth > 0 ? 'visible' : 'hidden'
						}}
					>
						<TicketsManagerTabs />
						<div 
							onMouseDown={handleMouseDown} 
							className={classes.dragger} 
						/>
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<Ticket />
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square elevation={0} className={classes.welcomeMsg}>
									<div className={classes.emptyState}>
										<div className={classes.emptyIcon}>
											<ChatBubbleOutlineIcon />
										</div>
										<div className={classes.emptyTitle}>Pronto para atender</div>
										<div className={classes.emptyText}>
											{i18n.t("chat.noTicketMessage")}
										</div>
									</div>
								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;
